import { Button, Flex, Icon, IconButton, Text } from '@chakra-ui/react';
import type { Editor } from '@tiptap/core';
import { FloatingMenu } from '@tiptap/react';
import { PlusIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { slashMenuCommands } from '../constants/slashMenuCommands';

export function DefaultFloatingMenu({ editor }: { editor: Editor | null }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (!editor) {
    return null;
  }

  return (
    <FloatingMenu editor={editor} tippyOptions={{ placement: 'left' }}>
      <Flex position="relative" ref={menuRef}>
        <IconButton
          aria-label="Insert block"
          icon={<PlusIcon />}
          size="xs"
          variant="tertiary"
          onClick={() => setIsOpen(!isOpen)}
        />
        {isOpen && (
          <Flex
            position="absolute"
            left="100%"
            top={0}
            ml={1}
            direction="column"
            zIndex={1100}
            bg="bg.surface"
            shadow="sm"
            gap={2}
            maxH="300px"
            minW="250px"
            overflowY="scroll"
            overflowX="hidden"
            py={2}
            px={0}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
          >
            {slashMenuCommands.length > 0 ? (
              slashMenuCommands.map((item) => (
                <Button
                  w="full"
                  variant="tertiary"
                  justifyContent="start"
                  gap={2}
                  borderRadius={0}
                  key={item.name}
                  onClick={() => {
                    item.command({ editor, range: editor.state.selection });
                    setIsOpen(false);
                  }}
                  leftIcon={<Icon as={item.icon} boxSize="24px" />}
                  fontWeight="normal"
                  textAlign="left"
                >
                  {item.name}
                </Button>
              ))
            ) : (
              <Text p={2} color="fg.muted">
                No result
              </Text>
            )}
          </Flex>
        )}
      </Flex>
    </FloatingMenu>
  );
}
